import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Linkedin, UserPlus, Link2, X, Loader2, Search } from 'lucide-react';
import api from '../../lib/api';

function age(mins) {
  if (mins == null) return '';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
}

export const UnmatchedReplyCard = ({ item, onLink, onCreate, onDismiss, busy }) => {
  const [picking, setPicking] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(item.suggested_leads || []);
  const [searching, setSearching] = useState(false);

  const search = (value) => {
    setQ(value);
    if (value.trim().length < 2) { setResults(item.suggested_leads || []); return; }
    setSearching(true);
    api.get(`/api/leads/?search=${encodeURIComponent(value.trim())}&limit=8`)
      .then((r) => setResults((r.data || []).map((l) => ({ id: l.id, name: l.name, company: l.company }))))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  };

  return (
    <Card className="border-amber-500/30" data-testid={`unmatched-card-${item.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-amber-600">
            <Linkedin className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold" data-testid="unmatched-name">{item.contact_name || 'Unknown sender'}</span>
              <span className="text-xs text-muted-foreground">{age(item.age_minutes)}</span>
              <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">Not linked to a lead</span>
            </div>
            <p className="mt-1 text-sm text-foreground/90" data-testid="unmatched-message">{item.message}</p>
            {item.linkedin_url && (
              <a href={item.linkedin_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-primary hover:underline">{item.linkedin_url}</a>
            )}

            {picking && (
              <div className="mt-3 rounded-lg border bg-muted/30 p-3" data-testid="unmatched-link-picker">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input value={q} onChange={(e) => search(e.target.value)} placeholder="Search leads by name…" className="pl-8" data-testid="unmatched-link-search" />
                </div>
                <div className="mt-2 space-y-1">
                  {searching && <div className="text-xs text-muted-foreground">Searching…</div>}
                  {!searching && results.length === 0 && <div className="text-xs text-muted-foreground">No matching leads</div>}
                  {results.map((l) => (
                    <button key={l.id} type="button" disabled={busy} onClick={() => onLink(item.id, l.id)}
                      className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                      data-testid={`unmatched-link-option-${l.id}`}>
                      <span>{l.name}{l.company ? <span className="text-muted-foreground"> · {l.company}</span> : null}</span>
                      <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant={picking ? 'secondary' : 'default'} disabled={busy} onClick={() => setPicking((v) => !v)} data-testid="unmatched-link-btn">
                <Link2 className="mr-1.5 h-3.5 w-3.5" /> {picking ? 'Cancel' : 'Link to lead'}
              </Button>
              <Button size="sm" variant="outline" disabled={busy || !item.contact_name} onClick={() => onCreate(item.id)} data-testid="unmatched-create-btn">
                {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <UserPlus className="mr-1.5 h-3.5 w-3.5" />} Create lead
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => onDismiss(item.id)} data-testid="unmatched-dismiss-btn">
                <X className="mr-1.5 h-3.5 w-3.5" /> Dismiss
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
